import React, { useEffect, useRef, useState } from "react";
import {
  BIRTH_MONTH_OPTIONS,
  combineBirthMonthYear,
  splitBirthMonthYear,
} from "../shared/utils/birthMonthYear";

/**
 * Month + year date-of-birth control (no day). Uses a month dropdown and a 4-digit year field
 * so users can type years like 1973 reliably (native type="month" is awkward for year entry).
 */
function BirthMonthYearInput({ id, value, onChange, disabled, required, className }) {
  const { month: propMonth, year: propYear } = splitBirthMonthYear(value);
  const [month, setMonth] = useState(propMonth);
  const [year, setYear] = useState(propYear);
  const yearFocusedRef = useRef(false);

  useEffect(() => {
    const { month: m, year: y } = splitBirthMonthYear(value);
    setMonth(m);
    if (!yearFocusedRef.current) {
      setYear(y);
    }
  }, [value]);

  function commit(nextMonth, nextYear) {
    const combined = combineBirthMonthYear(nextMonth, nextYear);
    if (combined) {
      onChange?.(combined);
      return;
    }
    if (!nextMonth && !nextYear) {
      onChange?.("");
    }
  }

  function handleMonthChange(e) {
    const nextMonth = (e.target.value || "").trim();
    setMonth(nextMonth);
    if (!nextMonth) {
      onChange?.("");
      return;
    }
    commit(nextMonth, year);
  }

  function handleYearChange(e) {
    const nextYear = String(e.target.value || "")
      .replace(/\D/g, "")
      .slice(0, 4);
    setYear(nextYear);
    commit(month, nextYear);
  }

  function handleYearBlur() {
    yearFocusedRef.current = false;
    const combined = combineBirthMonthYear(month, year);
    if (combined) {
      onChange?.(combined);
      return;
    }
    if (month && !year) {
      onChange?.("");
      return;
    }
    if (year.length > 0 && year.length < 4) {
      setYear("");
      onChange?.("");
    }
  }

  const monthId = id ? `${id}-month` : undefined;
  const yearId = id;

  return (
    <div className={`birth-month-year-input${className ? ` ${className}` : ""}`}>
      <select
        id={monthId}
        className="birth-month-year-input__month"
        value={month}
        onChange={handleMonthChange}
        disabled={disabled}
        required={required}
        aria-label="Birth month"
      >
        <option value="">Month…</option>
        {BIRTH_MONTH_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <input
        id={yearId}
        className="birth-month-year-input__year"
        type="text"
        inputMode="numeric"
        autoComplete="bday-year"
        placeholder="YYYY"
        maxLength={4}
        value={year}
        onChange={handleYearChange}
        onFocus={() => {
          yearFocusedRef.current = true;
        }}
        onBlur={handleYearBlur}
        disabled={disabled}
        required={required}
        aria-label="Birth year"
      />
    </div>
  );
}

export default BirthMonthYearInput;
